import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React from 'react'
import { Link } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import DealerClosedTag from '@/pages/RoIntake/components/DealerClosedTag'
import { SLA_LATE } from '@/pages/RoStatusDashboard/buckets'

import { formatDuration, GUIDELINE_HOURS, hoursWaiting, isAgedOut, isPastGuideline } from './queue'
import RowActionCells from './RowActionCells'

// Amber for the 48-hour guideline: a warning, not the contract's red (2026-10-01).
const GUIDELINE = { swatch: '#ef9f27', ink: '#633806' }

/**
 * One Outstanding List RO, in admin or dealer review (WARP-959). Its status is not
 * worked in place: the chat, the line items, and the actions needed all live on the RO
 * page, so the row says where the RO stands and opens that page. The dealer-closed flag
 * and attachments are worked here, then Save (WARP-966).
 */
const OutstandingRow = ({
  edit,
  now,
  onChange,
  onSave,
  permissions,
  row,
  saving,
  showDealer,
  today,
}) => {
  const details = row.outstandingList || {}
  const href = `/dealer/${row.dealer.id}/repair-order/${row.id}`
  const hours = hoursWaiting(row, now)
  const pastGuideline = isPastGuideline(row, now)
  const agedOut = isAgedOut(row, today)
  const isDraft = details.state === 'Draft'

  return (
    <tr
      className="border-charcoal-100 border-t align-middle"
      // Aged out costs the dealer the claim, so it gets the queue's strongest signal.
      style={agedOut ? { backgroundColor: SLA_LATE.fill } : undefined}
    >
      <td className="px-4 py-3">
        {/* The flag's own column is there for whoever can set it; others see the tag. */}
        {!permissions.canChangeStatus && row.dealerClosed && (
          <DealerClosedTag className="mr-1.5" />
        )}
        <Link
          className="text-charcoal-900 font-mono text-sm font-semibold hover:text-blue-800 hover:underline"
          to={href}
        >
          {row.roNumber}
        </Link>
        {isDraft && (
          // A draft is WPC's to finish before the dealer sees it (2026-09-28).
          <span className="bg-charcoal-200 text-charcoal-700 ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
            Draft
          </span>
        )}
        {showDealer && (
          <span className="text-charcoal-500 block truncate text-xs">{row.dealer.name}</span>
        )}
      </td>

      <td className="text-charcoal-700 whitespace-nowrap p-3 text-sm">
        {/* The year only when it is not this one. */}
        {dayjs(row.receivedAt).format(
          dayjs(row.receivedAt).isSame(now, 'year') ? 'MM/DD' : 'MM/DD/YY',
        )}
      </td>

      <td className="p-3">
        {/* Progress to the guideline. The words carry the state too, not the color alone. */}
        <div className="bg-charcoal-100 h-1.5 w-24 overflow-hidden rounded-full">
          <div
            className="h-full rounded-full"
            style={{
              width: `${Math.min(100, (hours / GUIDELINE_HOURS) * 100)}%`,
              backgroundColor: pastGuideline ? GUIDELINE.swatch : '#1d9e75',
            }}
          />
        </div>
        <span
          className={mergeClassNames(
            'mt-1 block whitespace-nowrap text-xs tabular-nums',
            pastGuideline ? 'font-bold' : 'text-charcoal-600',
          )}
          style={pastGuideline ? { color: GUIDELINE.ink } : undefined}
        >
          {formatDuration(hours)}
          {pastGuideline && ` · past ${GUIDELINE_HOURS} h`}
        </span>
      </td>

      <td className="whitespace-nowrap p-3 text-xs">
        {details.agesOut ? (
          <span
            className={agedOut ? 'font-bold' : 'text-charcoal-600'}
            style={agedOut ? { color: SLA_LATE.ink } : undefined}
          >
            {dayjs(details.agesOut).format('MM/DD/YY')}
            {agedOut && ' · aged out'}
          </span>
        ) : (
          <span className="text-charcoal-300">—</span>
        )}
      </td>

      <td className="p-3">
        <span
          className="text-charcoal-600 line-clamp-2 text-xs"
          title={(details.openActions || []).join(' · ')}
        >
          {(details.openActions || []).join(' · ')}
        </span>
      </td>

      <RowActionCells
        edit={edit}
        onChange={onChange}
        onSave={onSave}
        permissions={permissions}
        row={row}
        saving={saving}
      >
        <Link
          className="inline-block whitespace-nowrap rounded border-[1.5px] border-blue-800 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-blue-800 transition-colors hover:bg-blue-800 hover:text-white"
          to={href}
        >
          Open
        </Link>
      </RowActionCells>
    </tr>
  )
}

OutstandingRow.propTypes = {
  edit: PropTypes.object.isRequired,
  now: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  // What this user may do from the row; see `rowPermissions`.
  permissions: PropTypes.object.isRequired,
  row: PropTypes.object.isRequired,
  saving: PropTypes.bool,
  showDealer: PropTypes.bool,
  // Today as `YYYY-MM-DD`, for the age-out date.
  today: PropTypes.string.isRequired,
}

OutstandingRow.defaultProps = {
  saving: false,
  showDealer: false,
}

export default OutstandingRow
